import { computed, ref, shallowRef, watch } from 'vue'
import type { RadioSpot, RecordingSummary, Station } from '../models/generated/domain-models'
import { radioService } from '../services/api'
import { episodeTime, recordingTime } from '../utils/radio'
import { useToast } from './use-toast'

export type Version = 'raw' | 'episode'

const LEAD = 5
const SAVE_EVERY = 10
const QUIET = ['NotAllowedError', 'AbortError']

const element = shallowRef<HTMLAudioElement | null>(null)
const recording = shallowRef<RecordingSummary | null>(null)
const tuned = ref<Station | null>(null)
const live = computed(() => tuned.value !== null)
const version = ref<Version>('raw')
const part = ref(0)
const current = ref(0)
const playing = ref(false)
export const RATES = [1, 1.25, 1.5, 2]

const rate = ref(1)
const volume = ref(1)
const muted = ref(false)
const lost = ref(false)
const started = ref(false)
const spot = ref<RadioSpot | null>(null)
const toast = useToast()
let loaded = ''
let opened = ''
let heard = 0
let saved: number | null = null
let moved = false
let plan: { jumpAt: number; to: number; stopAt: number; jumped: boolean } | null = null

const parts = computed(() =>
  [...(recording.value?.status.parts ?? [])].sort((a, b) => a.offsetSeconds - b.offsetSeconds)
)
const offset = computed(() => parts.value[part.value]?.offsetSeconds ?? 0)
const seconds = computed(() =>
  version.value === 'raw' ? offset.value + current.value : current.value
)
const recordingSeconds = computed(() =>
  version.value === 'raw'
    ? seconds.value
    : recordingTime(recording.value?.audible ?? [], seconds.value)
)
const total = computed(() =>
  version.value === 'raw'
    ? (recording.value?.status.seconds ?? 0)
    : (recording.value?.editList?.durationSeconds ?? 0)
)

const url = (target: RecordingSummary, file: string) => radioService.fileUrl(target.name, file)

const source = (index: number) => {
  const audio = element.value
  const target = recording.value
  if (!audio || !target) return
  part.value = index
  audio.src = url(target, parts.value[index]?.file ?? 'raw.mp3')
}

const resume = () => {
  if (playing.value) play()
}

const seek = (to: number) => {
  const audio = element.value
  if (!audio || !recording.value) return
  lost.value = false
  const target = Math.min(Math.max(0, to), total.value)
  if (version.value === 'episode') {
    audio.currentTime = target
    current.value = target
    return
  }
  const index = parts.value.reduce((found, p, i) => (p.offsetSeconds <= target ? i : found), 0)
  if (index !== part.value) {
    source(index)
    resume()
  }
  audio.currentTime = target - offset.value
  current.value = audio.currentTime
}

const pick = (at: number) => {
  moved = true
  seek(at)
}

const store = (at: number) => {
  const name = recording.value?.name
  if (!name || saved === null) return
  heard = at
  saved = at
  radioService.savePosition(name, { seconds: at }).catch(() => undefined)
}

const remember = (at: number) => {
  if (!recording.value) return
  heard = at
  if (saved !== null && Math.abs(at - saved) >= SAVE_EVERY) store(at)
}

const keep = () => {
  if (heard !== saved) store(heard)
}

const place = (at: number) => {
  const target = recording.value
  heard = at
  if (target && at) seek(version.value === 'raw' ? at : episodeTime(target.audible, at))
}

const restore = (target: RecordingSummary) => {
  if (opened === target.name) return place(heard)
  opened = target.name
  heard = 0
  saved = null
  moved = false
  radioService.position(target.name).then(
    position => {
      if (opened !== target.name) return
      saved = position.seconds
      if (!moved) place(position.seconds)
    },
    () => toast.error('Could not find where you left the recording')
  )
}

const cue = (audio: HTMLAudioElement, target: RecordingSummary) => {
  if (version.value === 'raw') return source(0)
  const edit = target.editList
  audio.src = `${url(target, 'episode.m4a')}?v=${encodeURIComponent(edit?.renderedAt ?? '')}`
}

const open = () => {
  const audio = element.value
  const target = recording.value
  if (!audio || !target) return
  const key = `${target.name}|${version.value}|${version.value === 'episode' ? target.editList?.renderedAt : ''}`
  if (key === loaded) return
  loaded = key
  current.value = 0
  playing.value = false
  plan = null
  cue(audio, target)
  restore(target)
}

const speed = (audio: HTMLAudioElement, value: number) => {
  audio.defaultPlaybackRate = value
  audio.playbackRate = value
}

const load = (target: RecordingSummary, wanted: Version = 'raw') => {
  if (recording.value?.name !== target.name) {
    keep()
    started.value = false
  }
  recording.value = target
  version.value = wanted
  tuned.value = null
  if (element.value) speed(element.value, rate.value)
  open()
}

const play = () =>
  element.value?.play().catch((error: Error) => {
    if (!QUIET.includes(error.name)) toast.error('Could not play the audio')
  })

const swap = (wanted: Version) => {
  const target = recording.value
  if (!target || wanted === version.value) return
  const at = wanted === 'raw' ? recordingSeconds.value : episodeTime(target.audible, seconds.value)
  const was = playing.value
  load(target, wanted)
  seek(at)
  if (was) play()
}

const listen = (station: Station) => {
  const audio = element.value
  if (!audio) return
  keep()
  recording.value = null
  tuned.value = station
  loaded = 'live'
  plan = null
  current.value = 0
  audio.src = `${radioService.liveUrl}?station=${station}`
  speed(audio, 1)
  play()
}

const hangUp = () => {
  const audio = element.value
  if (!audio) return
  audio.pause()
  audio.removeAttribute('src')
  audio.load()
  playing.value = false
}

const toggle = () => {
  if (tuned.value) return playing.value ? hangUp() : listen(tuned.value)
  return playing.value ? element.value?.pause() : play()
}

const skip = (by: number) => pick(seconds.value + by)

const playFrom = (at: number, wanted: Version = 'raw') => {
  if (recording.value && version.value !== wanted) load(recording.value, wanted)
  plan = null
  pick(at)
  play()
}

const preview = (start: number, end: number) => {
  playFrom(start - LEAD)
  plan = { jumpAt: start, to: end, stopAt: end + LEAD, jumped: false }
}

const follow = () => {
  if (!plan) return
  if (!plan.jumped && seconds.value >= plan.jumpAt) {
    plan.jumped = true
    seek(plan.to)
    return
  }
  if (plan.jumped && seconds.value >= plan.stopAt) {
    plan = null
    element.value?.pause()
  }
}

const rejoin = () => (lost.value = false)

const listeners: Record<string, () => void> = {
  timeupdate: () => {
    current.value = element.value?.currentTime ?? 0
    remember(recordingSeconds.value)
    follow()
  },
  play: () => {
    playing.value = true
    started.value = true
    radioService
      .savePlayer({ recording: recording.value?.name ?? null, station: tuned.value })
      .catch(() => undefined)
  },
  pause: () => {
    playing.value = false
    if (!element.value?.ended) store(heard)
  },
  ended: () => {
    const next = parts.value[part.value + 1]
    if (version.value === 'raw' && next) {
      seek(Math.max(offset.value + (element.value?.duration || 0), next.offsetSeconds))
      return play()
    }
    playing.value = false
    store(0)
  },
}

const attach = (audio: HTMLAudioElement) => {
  element.value = audio
  Object.entries(listeners).forEach(([name, listener]) => audio.addEventListener(name, listener))
  speed(audio, rate.value)
  audio.volume = volume.value
  audio.muted = muted.value
  open()
}

const forget = () =>
  radioService
    .savePlayer({ recording: null, station: null })
    .catch(() => toast.error('Could not save closing the radio card'))

const reset = () => {
  keep()
  hangUp()
  loaded = ''
  opened = ''
  recording.value = null
  tuned.value = null
  version.value = 'raw'
  part.value = 0
  current.value = 0
  playing.value = false
  lost.value = false
  muted.value = false
  started.value = false
  plan = null
}

const close = () => {
  forget()
  reset()
}

const leave = () => {
  if (!started.value) reset()
}

const vacant = (name?: string) =>
  !started.value && !tuned.value && (!recording.value || recording.value.name === name)

export const edition = (target: RecordingSummary): Version =>
  target.editList && target.episodeBytes ? 'episode' : 'raw'

const recall = async () => {
  const card = await radioService.player().catch(() => null)
  if (!card) return
  spot.value ??= card.spot
  const kept = card.recording
  if ((!kept && !card.station) || !vacant(kept?.name)) return
  if (kept && !recording.value) load(kept, edition(kept))
  tuned.value = card.station ?? null
  started.value = true
}

const pin = (at: RadioSpot) => radioService.saveSpot(at).catch(() => undefined)

watch(rate, value => {
  if (element.value && !live.value) speed(element.value, value)
})

watch(volume, value => {
  if (element.value) element.value.volume = value
})

watch(muted, value => {
  if (element.value) element.value.muted = value
})

export const useRadioPlayer = () => ({
  recording,
  tuned,
  live,
  version,
  seconds,
  recordingSeconds,
  total,
  playing,
  rate,
  volume,
  muted,
  lost,
  started,
  spot,
  rejoin,
  attach,
  close,
  leave,
  recall,
  pin,
  load,
  swap,
  listen,
  toggle,
  seek: pick,
  skip,
  playFrom,
  preview,
})
